import { Fragment, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { Button, Card, Badge, Input, Select, Modal, LoadingSkeleton } from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  useBoms,
  useCreateBom,
  useUpdateBom,
  useDeleteBom,
  useActivateBom,
  type Bom,
  type BomInput,
  type BomUpdateInput,
} from './useBoms';

const statusVariant: Record<string, 'success' | 'warning' | 'neutral' | 'error'> = {
  ACTIVE: 'success',
  DRAFT: 'neutral',
  OBSOLETE: 'error',
};

const statusLabel: Record<string, string> = {
  ACTIVE: 'Ativo',
  DRAFT: 'Rascunho',
  OBSOLETE: 'Obsoleto',
};

interface BomFormData {
  productId: string;
  description: string;
  effectiveSince: string;
  components: {
    productId: string;
    productName: string;
    quantity: number;
    unit: string;
    unitCost: number;
  }[];
}

const emptyFormData: BomFormData = {
  productId: '',
  description: '',
  effectiveSince: '',
  components: [],
};

export function BomsPage(): JSX.Element {
  const { addToast } = useToast();

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const pageSize = 20;

  const [modalOpen, setModalOpen] = useState(false);
  const [editingBom, setEditingBom] = useState<Bom | null>(null);
  const [formData, setFormData] = useState<BomFormData>(emptyFormData);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { data, isLoading, error } = useBoms({
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    search: search || undefined,
    page,
    limit: pageSize,
  });

  const createBom = useCreateBom();
  const updateBom = useUpdateBom();
  const deleteBom = useDeleteBom();
  const activateBom = useActivateBom();

  const openCreateModal = (): void => {
    setEditingBom(null);
    setFormData(emptyFormData);
    setFormErrors([]);
    setModalOpen(true);
  };

  const openEditModal = (bom: Bom): void => {
    setEditingBom(bom);
    setFormData({
      productId: bom.productId,
      description: bom.description,
      effectiveSince: bom.effectiveSince?.split('T')[0] ?? '',
      components: bom.components.map((c) => ({
        productId: c.productId,
        productName: c.productName ?? '',
        quantity: c.quantity,
        unit: c.unit,
        unitCost: c.unitCost,
      })),
    });
    setFormErrors([]);
    setModalOpen(true);
  };

  const closeModal = (): void => {
    setModalOpen(false);
    setEditingBom(null);
    setFormData(emptyFormData);
    setFormErrors([]);
  };

  const addComponentRow = (): void => {
    setFormData((prev) => ({
      ...prev,
      components: [
        ...prev.components,
        {
          productId: '',
          productName: '',
          quantity: 1,
          unit: 'UN',
          unitCost: 0,
        },
      ],
    }));
  };

  const updateComponent = (
    index: number,
    field: keyof (typeof formData.components)[0],
    value: string | number,
  ): void => {
    setFormData((prev) => {
      const updated = [...prev.components];
      const item = { ...updated[index] } as (typeof formData.components)[0];
      if (!item) return prev;

      if (field === 'quantity') {
        item.quantity = typeof value === 'number' ? value : parseFloat(value) || 0;
      } else if (field === 'unitCost') {
        item.unitCost = typeof value === 'number' ? value : parseFloat(value) || 0;
      } else {
        (item as Record<string, unknown>)[field] = value;
      }

      updated[index] = item;
      return { ...prev, components: updated };
    });
  };

  const removeComponent = (index: number): void => {
    setFormData((prev) => ({
      ...prev,
      components: prev.components.filter((_, i) => i !== index),
    }));
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];
    if (!formData.productId.trim()) errors.push('Informe o ID do produto');
    if (!formData.description.trim()) errors.push('Informe a descrição');
    if (formData.components.length === 0) errors.push('Adicione pelo menos um componente');
    if (formData.components.some((c) => c.quantity <= 0)) {
      errors.push('Quantidade dos componentes deve ser maior que zero');
    }
    setFormErrors(errors);
    return errors.length === 0;
  };

  const handleSave = async (): Promise<void> => {
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const components = formData.components.map((c) => ({
        productId: c.productId,
        productName: c.productName || undefined,
        quantity: c.quantity,
        unit: c.unit,
        unitCost: c.unitCost,
      }));

      if (editingBom) {
        const updateInput: BomUpdateInput = {
          id: editingBom.id,
          description: formData.description,
          effectiveSince: formData.effectiveSince
            ? new Date(formData.effectiveSince).toISOString()
            : undefined,
          components: components.map((c) => ({
            productId: c.productId,
            quantity: c.quantity,
            unit: c.unit,
            unitCost: c.unitCost,
          })),
        };
        await updateBom.mutateAsync(updateInput);
        addToast('Estrutura atualizada com sucesso', 'success');
      } else {
        const createInput: BomInput = {
          productId: formData.productId,
          description: formData.description,
          effectiveSince: formData.effectiveSince
            ? new Date(formData.effectiveSince).toISOString()
            : undefined,
          components,
        };
        await createBom.mutateAsync(createInput);
        addToast('Estrutura criada com sucesso', 'success');
      }

      closeModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar estrutura';
      setFormErrors([message]);
      addToast(message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    try {
      await deleteBom.mutateAsync(id);
      addToast('Estrutura excluida com sucesso', 'success');
      setDeleteConfirmId(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao excluir estrutura';
      addToast(message, 'error');
    }
  };

  const handleActivate = async (id: string): Promise<void> => {
    try {
      await activateBom.mutateAsync(id);
      addToast('Estrutura ativada com sucesso', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao ativar estrutura';
      addToast(message, 'error');
    }
  };

  /* Linha expansivel: o payload da lista ja traz `components[]` completo,
     entao o detalhe da referencia cabe aqui sem chamada nova. */
  const [expandedId, setExpandedId] = useState<string | null>(null);

  /* `Bom` traz `productId`, nao o nome do produto — a coluna mostrava UUID.
     Mesmo defeito que apareceu na tela de Lotes; mesma correcao. */
  const productsQ = useQuery<{ id: string; name: string }[]>({
    queryKey: ['products', 'bom-names'],
    queryFn: async () => apiClient.get<{ id: string; name: string }[]>('/products'),
  });
  const productName = (id: string): string =>
    (productsQ.data ?? []).find((p) => p.id === id)?.name ?? id;

  const renderActions = (bom: Bom): ReactNode => (
    <div className="action-buttons">
      <Button
        variant="outline"
        size="sm"
        onClick={() => openEditModal(bom)}
      >
        Editar
      </Button>
      {bom.status === 'DRAFT' && (
        <Button
          variant="primary"
          size="sm"
          onClick={() => handleActivate(bom.id)}
        >
          Ativar
        </Button>
      )}
      {bom.status === 'DRAFT' && (
        <>
          {deleteConfirmId === bom.id ? (
            <div className="confirm-delete">
              <span>Tem certeza?</span>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleDelete(bom.id)}
              >
                Sim
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteConfirmId(null)}
              >
                Não
              </Button>
            </div>
          ) : (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setDeleteConfirmId(bom.id)}
            >
              Excluir
            </Button>
          )}
        </>
      )}
    </div>
  );

  if (isLoading && !data) {
    return (
      <div className="boms-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Estruturas (BOM)</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Estruturas de produto e componentes.</p>
          </div>
        </div>
        <Card>
          <LoadingSkeleton />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="boms-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Estruturas (BOM)</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Estruturas de produto e componentes.</p>
          </div>
        </div>
        <Card>
          <div className="error-state" role="alert">
            <p>Erro ao carregar estruturas: {(error as Error).message}</p>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const boms = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="boms-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Estruturas (BOM)</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Estruturas de produto e componentes.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="primary" onClick={openCreateModal}>
            Nova estrutura
          </Button>
        </div>
      </div>

      <Card>
        <div className="filters">
          <Input
            label=""
            placeholder="Buscar por produto ou descrição..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          <Select
            label=""
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="ALL">Todos os status</option>
            <option value="DRAFT">Rascunho</option>
            <option value="ACTIVE">Ativo</option>
            <option value="OBSOLETE">Obsoleto</option>
          </Select>
        </div>

        {boms.length === 0 ? (
          <div className="empty-state">
            <p>Nenhuma estrutura encontrada</p>
            <Button variant="primary" onClick={openCreateModal}>
              Criar primeira estrutura
            </Button>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="bom-table">
                <thead>
                  <tr>
                    <th style={{ width: '32px' }} aria-label="Expandir" />
                    <th>Produto</th>
                    <th>Descrição</th>
                    <th>Versão</th>
                    <th>Componentes</th>
                    <th style={{ textAlign: 'right' }}>Custo total</th>
                    <th>Status</th>
                    <th>Criado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {boms.map((bom) => {
                    const open = expandedId === bom.id;
                    return (
                      <Fragment key={bom.id}>
                        <tr
                          style={{ cursor: 'pointer' }}
                          onClick={() => setExpandedId(open ? null : bom.id)}
                        >
                          <td>
                            <span
                              className="material-symbols-outlined text-on-surface-variant"
                              style={{ fontSize: '18px', display: 'inline-block' }}
                              aria-hidden="true"
                            >
                              {open ? 'expand_more' : 'chevron_right'}
                            </span>
                          </td>
                          <td>{productName(bom.productId)}</td>
                          <td>{bom.description}</td>
                          <td className="font-label-md text-label-md">v{bom.version}</td>
                          <td>{bom.components.length} {bom.components.length === 1 ? 'item' : 'itens'}</td>
                          <td
                            style={{
                              textAlign: 'right',
                              fontVariantNumeric: 'tabular-nums',
                              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                            }}
                          >
                            {formatCurrency(bom.totalCost)}
                          </td>
                          <td>
                            <Badge variant={statusVariant[bom.status] ?? 'neutral'}>
                              {statusLabel[bom.status] ?? bom.status}
                            </Badge>
                          </td>
                          <td>{formatDate(bom.createdAt)}</td>
                          <td onClick={(e) => e.stopPropagation()}>{renderActions(bom)}</td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={9} style={{ padding: 0 }}>
                              <div className="bom-components">
                                <div className="bom-components__head">
                                  <h4>Componentes da estrutura</h4>
                                  <span>
                                    {bom.components.length} {bom.components.length === 1 ? 'item' : 'itens'} · custo total{' '}
                                    {formatCurrency(bom.totalCost)}
                                  </span>
                                </div>
                                {bom.components.length === 0 ? (
                                  <p className="bom-components__empty">
                                    Esta estrutura ainda nao tem componentes.
                                  </p>
                                ) : (
                                  <table className="bom-table bom-table--nested">
                                    <thead>
                                      <tr>
                                        <th>Componente</th>
                                        <th>SKU</th>
                                        <th style={{ textAlign: 'right' }}>Qtd</th>
                                        <th>Unid.</th>
                                        <th style={{ textAlign: 'right' }}>Custo unit.</th>
                                        <th style={{ textAlign: 'right' }}>Custo est.</th>
                                        <th style={{ textAlign: 'right' }}>% perda</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {bom.components.map((c, i) => (
                                        <tr key={`${bom.id}-${c.productId}-${i}`}>
                                          <td>{c.productName ?? productName(c.productId)}</td>
                                          <td className="font-label-md text-label-md">
                                            {c.sku ?? '\u2014'}
                                          </td>
                                          <td className="bom-num">{c.quantity}</td>
                                          <td>{c.unit}</td>
                                          <td className="bom-num">{formatCurrency(c.unitCost)}</td>
                                          <td className="bom-num">
                                            {formatCurrency(c.quantity * c.unitCost)}
                                          </td>
                                          <td className="bom-num">
                                            {c.wastePercent ? `${c.wastePercent}%` : '\u2014'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Anterior
                </button>
                <span className="page-info">
                  Pagina {page} de {totalPages} ({total} registros)
                </span>
                <button
                  className="page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingBom ? 'Editar estrutura' : 'Nova estrutura'}
        size="lg"
        footer={
          <div className="modal-actions">
            <Button variant="outline" onClick={closeModal} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSave} loading={isSubmitting}>
              {editingBom ? 'Salvar alteracoes' : 'Criar estrutura'}
            </Button>
          </div>
        }
      >
        <div className="form-fields">
          <div className="form-row">
            <Input
              label="ID do produto"
              value={formData.productId}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, productId: e.target.value }))
              }
              placeholder="UUID do produto"
              disabled={!!editingBom}
            />
            <Input
              label="Descrição"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder="Descrição da estrutura"
            />
          </div>
          <div className="form-row">
            <Input
              label="Vigencia desde"
              type="date"
              value={formData.effectiveSince}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, effectiveSince: e.target.value }))
              }
            />
          </div>

          <div className="components-section">
            <div className="section-header">
              <h3>Componentes</h3>
              <Button variant="outline" size="sm" onClick={addComponentRow}>
                + Adicionar
              </Button>
            </div>

            {formData.components.length === 0 && (
              <p className="empty-components">
                Nenhum componente adicionado. Clique em &quot;Adicionar&quot; para incluir.
              </p>
            )}

            {formData.components.map((comp, index) => (
              <div key={index} className="component-row">
                <Input
                  label="ID do produto"
                  value={comp.productId}
                  onChange={(e) => updateComponent(index, 'productId', e.target.value)}
                  placeholder="UUID"
                />
                <Input
                  label="Nome"
                  value={comp.productName}
                  onChange={(e) => updateComponent(index, 'productName', e.target.value)}
                  placeholder="Nome do componente"
                />
                <Input
                  label="Qtd"
                  type="number"
                  value={String(comp.quantity)}
                  onChange={(e) =>
                    updateComponent(index, 'quantity', parseFloat(e.target.value) || 0)
                  }
                  min="0"
                  step="0.001"
                />
                <Input
                  label="Unidade"
                  value={comp.unit}
                  onChange={(e) => updateComponent(index, 'unit', e.target.value)}
                  placeholder="UN"
                />
                <Input
                  label="Custo unit."
                  type="number"
                  value={String(comp.unitCost)}
                  onChange={(e) =>
                    updateComponent(index, 'unitCost', parseFloat(e.target.value) || 0)
                  }
                  min="0"
                  step="0.01"
                />
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeComponent(index)}
                >
                  Remover
                </Button>
              </div>
            ))}
          </div>

          {formErrors.length > 0 && (
            <div className="form-errors" role="alert">
              {formErrors.map((err, i) => (
                <p key={i}>{err}</p>
              ))}
            </div>
          )}
        </div>
      </Modal>

      <style>{`
        .bom-components { background: #f7f9fb; padding: 1rem 1.25rem 1.25rem; border-bottom: 1px solid #eceef0; }
        .bom-components__head { display: flex; align-items: baseline; justify-content: space-between; gap: 1rem; margin-bottom: 0.75rem; }
        .bom-components__head h4 { margin: 0; font-size: 14px; font-weight: 600; color: #191c1e; }
        .bom-components__head span { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; color: #43474e; }
        .bom-components__empty { margin: 0; font-size: 0.875rem; color: #43474e; }
        .bom-table--nested { background: #ffffff; border: 1px solid #c4c6cf; border-radius: 4px; }
        .bom-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; }

        .boms-page {
          padding: 1.5rem;
        }
        .filters {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .filters > *:first-child {
          flex: 1;
        }
        .table-wrapper {
          overflow-x: auto;
        }
        .bom-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }
        .bom-table th {
          text-align: left;
          padding: 0.75rem 1rem;
          background: #f2f4f6;
          color: #43474e;
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5;
          white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .bom-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #eceef0;
          color: #191c1e;
          white-space: nowrap;
        }
        .bom-table tbody tr:hover td {
          background: #eceef0;
        }
        .action-buttons {
          display: flex;
          gap: 0.375rem;
          align-items: center;
        }
        .confirm-delete {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.75rem;
          color: #ba1a1a;
        }
        .pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          padding: 1rem 0;
        }
        .page-btn {
          padding: 0.375rem 0.75rem;
          border: 1px solid #c4c6cf;
          background: #ffffff;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          font-family: inherit;
        }
        .page-btn:disabled {
          opacity: 0.5;
          cursor: default;
        }
        .page-info {
          font-size: 0.8125rem;
          color: #43474e;
        }
        .error-state {
          padding: 2rem;
          text-align: center;
          color: #ba1a1a;
        }
        .empty-state {
          padding: 3rem 2rem;
          text-align: center;
          color: #74777f;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }
        .modal-actions {
          display: flex;
          gap: 0.75rem;
        }
        .form-fields {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }
        .components-section {
          border-top: 1px solid #e0e3e5;
          padding-top: 1rem;
        }
        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 0.75rem;
        }
        .section-header h3 {
          margin: 0;
          font-size: 1rem;
          color: #191c1e;
        }
        .empty-components {
          text-align: center;
          color: #74777f;
          padding: 1rem 0;
          font-size: 0.875rem;
        }
        .component-row {
          display: grid;
          grid-template-columns: 1fr 1.5fr 80px 80px 100px 80px;
          gap: 0.5rem;
          align-items: end;
          padding: 0.5rem 0;
          border-bottom: 1px solid #eceef0;
        }
        .form-errors {
          padding: 1rem;
          background: #ffdad6;
          border: 1px solid #ffb4ab;
          border-radius: 4px;
        }
        .form-errors p {
          color: #ba1a1a;
          font-size: 0.875rem;
          margin: 0.25rem 0;
        }
      `}</style>
    </div>
  );
}
